import type {
  NxTableSummaryColumn,
  NxTableSummaryCellSlotProps,
  NxTableSummaryValue,
} from './tableSummary';
import type { ComputedRef, InjectionKey, Ref, VNode } from 'vue';
import type { NxHue } from '../lib/hue';
import type { NxTableScrollState } from './composables/useScrollState';
import type {
  CellId,
  CellNavigationIntent,
  EditCellRegisterFocus,
  FocusFrom,
} from './composables/types';
import type {
  FilterModifier,
  NxTableColumnFilter,
  NxTableFilterGroup,
  NxTableFilterKind,
  NxTableFilterOperator,
} from './filterModel';

export type {
  CellId,
  CellNavigationIntent,
  EditCellRegisterFocus,
  FocusFrom,
} from './composables/types';
export * from './filterModel';

export type SortOrder = 'asc' | 'desc';

export type ValuesInMap<T_> = T_ extends Map<unknown, infer I> ? I : never;

export type OrNull<T> = {
  [P in keyof T]: T[P] | null;
};
export type PartialOrNull<T> = Partial<OrNull<T>>;

export type EditCellInputType = 'text' | 'multiline' | 'select' | 'wysiwyg';

/** Resolved render mode of one body cell. */
export type CellMode = 'edit' | 'display' | 'blank';

export interface TableColumnConfig<
  T extends Record<string, unknown>,
  Column extends keyof T | (string & {}) = keyof T | (string & {}),
  /** additional helper type as 1st class ts-support in vue is a joke */
  T_keyof_T = T[keyof T],
> extends NxTableSummaryColumn<T> {
  /** Presentation only: the summary never participates in editing or navigation. */
  summaryCell?: (props: NxTableSummaryCellSlotProps) => VNode;
  /** unique identifier of the column */
  columnId: Column;
  /** will be used in the show/hide columns dialog and if no `headerCell`-slot is given, this text will be displayed. If not given the `columnId` will be used */
  displayName?: string;
  /** wether the column should be resizable */
  resizeable: boolean;
  /** wether the column should be drag/drop-able; defaults to `true` */
  reorderable: boolean;
  /** wether the column should be sort-able; defaults to `true` */
  sortable: boolean;
  /** wether the column should be filter-able. If `true` the column must define a `filterCell`-slot. Defaults to `false`. */
  filterable: boolean;
  /**
   * Value semantics of the column for the header filter popover: drives the
   * operator set (string: contains/starts with/…, number: >/</=/between,
   * date: from–to, select: one of, bool: yes/no) and the value inputs.
   * Defaults to `'string'`. Only relevant when `filterable` is `true`.
   */
  filterKind?: NxTableFilterKind;
  /**
   * Restricts the filter operators the column offers — in the header funnel (its
   * condition editor and which quick controls show) AND in the filter wizard.
   * For a server that runs only some operators. ONE rule everywhere (see
   * `supportedFilterOperators`): no list, an empty list, a list naming nothing the
   * funnel supports for the kind, or one that leaves it neither the checklist
   * (`oneOf`) nor an editor operator (`['noneOf']` alone on a text column) is
   * unrestricted (all operators of `filterKind`);
   * otherwise the listed operators of the kind plus, for a text, select or bool
   * column, the checklist operators `oneOf` / `noneOf`.
   *  - `oneOf` is the one-click value checklist: without it a text, select or bool
   *    column has no checklist and filters through the condition editor;
   *  - `noneOf` is its "exclude" switch: without it the switch is not offered, and
   *    a stored exclusion is not carried into the checklist;
   *  - `contains` is the live search of a text column's checklist: without it the
   *    search box only narrows the values and Enter filters nothing;
   *  - a text column restricted to `['oneOf']` is the checklist only: no "advanced"
   *    editor, and the wizard does not offer the column.
   * Read once, when the column registers.
   */
  filterOperators?: readonly NxTableFilterOperator[];
  /**
   * How several conditions of this column may combine in the header funnel.
   * Default `['and', 'or']`; `['and']` offers no "or" (the switch is not shown and
   * a stored "or" reads as "and"). The filter wizard honours it for a group made of
   * conditions on that one column; its groups across columns stay free. Read once,
   * when the column registers.
   */
  filterModifiers?: readonly FilterModifier[];
  /**
   * Selectable values for `filterKind: 'select'` (usually the column's
   * options). Ignored for other kinds.
   */
  filterOptions?: string[];
  /**
   * Display label for a `filterOptions` value in the filter popover/chips
   * (e.g. enum code → localized label). Raw value when omitted.
   *
   * The select-shaped column wrappers ALSO read it as the plain-text twin of
   * their cell options when no `optionText` is given, and feed `optionText`
   * back into it when only that is given — one function, both channels, so a
   * column states its words once instead of showing "Teillieferung" in its
   * dropdowns and `partial_delivery` in the checklist beside them (I-1016). A
   * column whose two channels genuinely read differently — a short cell label
   * and a spelled-out filter label, or `filterOptions` carrying values the cells
   * never offer — states BOTH props; each wins in its own channel.
   */
  filterOptionLabel?: (value: string) => string;
  /**
   * Value source for the filter popover's checklist on string/select columns:
   * returns the pickable values for a search term (may be async — e.g. a
   * GraphQL distinct-values query). When omitted, NxTable falls back to the
   * distinct display values of the currently loaded rows.
   */
  filterValueSource?: (search: string) => string[] | Promise<string[]>;
  /**
   * Hide the search box in the header-filter checklist (it shows by default;
   * bool columns never get one). Opt-OUT on purpose: column wrappers spread
   * their props, and Vue casts absent Boolean props to `false` — an opt-in
   * "searchable" flag would silently turn the search off everywhere.
   */
  filterSearchHidden?: boolean;
  /**
   * Opt-OUT of the table's `rowHref` for this column: its display cells are drawn
   * as the plain `div` they were, not as a link. Set it on a column whose cell slot
   * renders a button, a link, a checkbox or any other interactive content (an
   * `<a>` cannot contain it). Opt-out on purpose, like `filterSearchHidden`:
   * column wrappers spread their props and Vue casts an absent Boolean to `false`,
   * so an opt-in would switch itself off. Default `false` (the column is a link).
   */
  rowLinkDisabled?: boolean;
  /** wether the column should be edit-able. If `true` the column will use the `editCell`-slot in edit-mode. Otherwise the normal `cell` will be used. Defaults to `true`. */
  editable: boolean;
  /**
   * Exclude this column from the spreadsheet-style Tab sequence. Defaults to
   * `false`. Use it for secondary commands such as a manually editable
   * position number: pointer and arrow navigation still reach the editor,
   * while rapid data entry skips it. This is deliberately opt-out because Vue
   * casts absent Boolean wrapper props to `false`.
   */
  tabNavigationDisabled: boolean;
  /**
   * Keep pointer controls in a row-scoped lazy edit cell interactive while a
   * sibling cell owns the cursor. The control itself must stay out of native
   * Tab order. Defaults to `false`.
   */
  inactivePointerInteractive: boolean;
  /**
   * Mount this column's edit cell in EVERY row, also when the table runs with
   * `lazyEditCells` — for a light control that acts on one click, such as the
   * checkbox variant of `NxTableBoolColumn`: a row that is not the cursor row
   * would otherwise show its read-only display cell, and the first click only
   * activated the row. Combine it with `inactivePointerInteractive`; the
   * control stays out of native Tab order. Defaults to `false`.
   */
  eagerEditCell: boolean;
  /**
   * Per-row cell mode override. Where the edit path applies (table edit-mode
   * + column editable + table onEdit): `'edit'` renders the editCell
   * (default), `'display'` renders the display cell, `'blank'` renders an
   * empty cell (the cell-anchor stays, so keyboard navigation is unaffected).
   * Everywhere else only `'blank'` is honored: a cell with no meaningful value
   * for its row stays empty on a read-only table and in a read-only column
   * too. The hook can only downgrade — `'edit'` never upgrades.
   */
  cellMode?: (rowData: T) => CellMode;
  /** wether the column should be visible or hidden by default; defaults to `true` */
  defaultVisible: boolean;
  /** The cell/editCell layout (flex-direction) */
  layout: 'column' | 'row';
  /**
   * Horizontal alignment of the column's header label, display cell and edit
   * input. `'end'` right-aligns the content — use it for numeric/currency
   * columns so the values read down the column. Defaults to `'start'`.
   */
  align?: 'start' | 'end';
  /**
   * Resize/stored-width floor in px. The header-label floor still applies;
   * this covers cell content (value + trailing lookup `…`) so columns like
   * Pos. / Einheit / MwSt cannot be squeezed until the value clips.
   */
  minWidth?: number;
  /** value that will be used for the csv export and if no `cell`-slot is given this text will be displayed */
  toCsvString: (rowData: T, rowIndex: number) => string;
  /** callback that will be called when a saved query is loaded. This is needed as the filterCell needs to be updated visually. Only required if `filterable` is `true`. */
  onQueryLoaded?: (value: (T_keyof_T | string)[]) => void;
  /** if not provided the `displayName` will be displayed */
  headerCell?: (props: object) => VNode;
  /** only needed if `filterable` is `true` */
  filterCell?: (props: {
    setFilterValue: (value: (T_keyof_T | string)[]) => void;
    disabled: boolean;
    registerFocus: EditCellRegisterFocus;
    focusAbove: () => void;
    focusBelow: () => void;
    focusLeft: (intent?: CellNavigationIntent) => boolean | void;
    focusRight: (intent?: CellNavigationIntent) => boolean | void;
  }) => VNode;
  /** only needed if `edit-mode` is `false` on the table; if not provided the evaluated `toCsvString`-value will be displayed */
  cell?: (props: { rowData: T; rowIndex: number }) => VNode;
  /** only needed if `create` is in the `allowedActions` of the table */
  createCell?: (props: {
    partialRowData: Partial<T>;
    setPartialRowData: (partialRowData: PartialOrNull<T>) => void;
  }) => VNode;
  /** only needed if `edit-mode` is `true` on the table */
  editCell?: (props: {
    rowData: T;
    rowIndex: number;
    /**
     * Commit this cell's new value(s).
     *
     * **The keys must be column ids.** The type says `Partial<T>`, i.e. data
     * fields, and for every column shipped here the two coincide because each one
     * emits `{ [props.columnId]: value }`. Consumers that read the payload as
     * columns rely on it — `cellSaveStatus` resolves the keys against
     * `(rowId, columnId)` to mark the cell that is saving. A custom `editCell`
     * writing a different field (or a foreign key plus its denormalised name)
     * would mark the wrong cell, or none.
     */
    onEdit: (newValues: Partial<T>) => Promise<void>;
    /**
     * Register a focus-handler for this cell. The handler receives a
     * `FocusFrom` direction hint and is responsible for focusing the
     * correct input and placing the caret/selection. Pass `null` to
     * unregister. For the common case (one input, function-template-ref)
     * use `defaultEditCellFocusRef`. For multi-input cells use both
     * `'first'` and `'last'` slots — the helper composes one handler.
     */
    registerFocus: EditCellRegisterFocus;
    /**
     * Commit the pending edit and move focus to the neighbor cell above.
     * The target cell's registered handler is called with `'bottom'`.
     * On the first row this blurs the current input so focus escapes
     * the table (user can Tab into whatever sits above).
     */
    focusAbove: () => void;
    /**
     * Commit and move focus to the cell below. The target is called with
     * `'top'`. Past the last row triggers row creation via the table's
     * `onCreate` callback (if defined).
     */
    focusBelow: () => void;
    /**
     * Commit and move focus to the cell to the left. The target is called
     * with `'bottom'`. At the first column wraps to the previous row's
     * last column. Pass `'tab'` from a Shift+Tab handler to use the content-only
     * Tab order and leave the grid at its first editor.
     */
    focusLeft: (intent?: CellNavigationIntent) => boolean | void;
    /**
     * Commit and move focus to the cell to the right. The target is called
     * with `'top'`. At the last column wraps to the next row's first
     * column. Past the last row normal navigation triggers row creation; the
     * `'tab'` intent instead leaves the grid without creating.
     */
    focusRight: (intent?: CellNavigationIntent) => boolean | void;
  }) => VNode;
}

/**
 * Resolve the render mode of one body cell. The edit path applies only when
 * the table is in edit mode, the column is editable and the table has an
 * onEdit handler — outside of that the cell renders as display, or blank when
 * `cellMode` says so (`cellMode` can only downgrade, never upgrade).
 */
export function resolveCellMode<T extends Record<string, unknown>>(
  column: Pick<TableColumnConfig<T>, 'editable' | 'cellMode'>,
  editMode: boolean,
  hasOnEdit: boolean,
  rowData: T
): CellMode {
  if (!editMode || !column.editable || !hasOnEdit) {
    return column.cellMode?.(rowData) === 'blank' ? 'blank' : 'display';
  }
  return column.cellMode?.(rowData) ?? 'edit';
}

/** Reserved ID for the leftmost indicator/row-selection column. */
export const INDICATOR_COLUMN_ID = '__indicator__' as const;
/**
 * Reserved ID for the checkbox column of `rowSelectionMode: 'checkbox'`, right
 * behind the indicator column. It exists only in that mode.
 */
export const SELECT_COLUMN_ID = '__select__' as const;
/**
 * Reserved ID of the expand toggle of `expandableRows`. The toggle sits in the
 * indicator column's cell, at the table's edge; for the arrow keys it is a stop
 * of its own, right after the row anchor.
 */
export const EXPAND_COLUMN_ID = '__expand__' as const;
/** Reserved ID for the rightmost row-action column. */
export const ROW_ACTION_COLUMN_ID = '__rowAction__' as const;
/**
 * Key of the implicit group of a grouped table (`groupBy`): it collects the rows
 * whose key is `null`/`undefined` or not listed in `groups`. Reserved — never
 * use it as a key of your own.
 */
export const UNGROUPED_GROUP_KEY = '__ungrouped__' as const;
/** Reserved row ID for the filter row; never collides with data row IDs. */
export const FILTER_ROW_ID = '__filter__' as const;
export const SETTINGS_ROW_ID = '__settings__' as const;

export type VirtualColumnConfig<T extends Record<string, unknown>> = {
  columnId: typeof INDICATOR_COLUMN_ID | typeof ROW_ACTION_COLUMN_ID;
  sticky: 'left' | 'right';
  /** Pixel width (fixed). */
  width: number;
  /** If false, navigateFocus skips this column. */
  participatesInNavigation: boolean;
  /** Header cell (today: settings dropdown / filter-modifier toggle). */
  headerSlot?: () => VNode;
  /** Filter row cell (today: filter-reset / filter-modifier). */
  filterSlot?: () => VNode;
  /** Body row cell. */
  cellSlot: (props: {
    rowData: T;
    rowIndex: number;
    registerFocus: EditCellRegisterFocus;
    focusAbove: () => void;
    focusBelow: () => void;
    focusLeft: () => void;
    focusRight: () => void;
  }) => VNode;
};

type FocusRef = (inst: unknown) => void;

/**
 * Resolve the native focusable element from a component instance or a
 * DOM element. Honors a component's `inputElement` / `divElement` refs
 * (if exposed) before falling back to a DOM query. For multi-root
 * components, `$el` is the leading comment marker; walk to the first
 * real element sibling before querying.
 */
function resolveInputElement(inst: unknown): HTMLElement | null {
  if (!inst) return null;
  if (inst instanceof HTMLElement) return inst;
  const i = inst as {
    inputElement?: HTMLElement | null;
    divElement?: HTMLElement | null;
    $el?: Node | null;
  };
  if (i.inputElement instanceof HTMLElement) return i.inputElement;
  if (i.divElement instanceof HTMLElement) return i.divElement;
  const selector = 'input, textarea, select, [contenteditable="true"], [tabindex]';
  let node: Node | null = i.$el ?? null;
  while (node && node.nodeType !== Node.ELEMENT_NODE) node = node.nextSibling;
  const root = node as HTMLElement | null;
  if (root?.matches?.(selector)) return root;
  return root?.querySelector<HTMLElement>(selector) ?? null;
}

/**
 * Focus the element and, for `from !== 'click'`, select the whole content
 * (if it's a standard input/textarea). Power-user default: keyboard-arrow
 * entry into a cell selects the value so the next keystroke replaces it.
 *
 * Selection is applied *before* `focus()` so a `focus`-handler in the
 * input component (e.g. NxCurrencyInput's reformat-on-focus) can observe
 * `selectionStart === 0 && selectionEnd === length` and preserve the
 * select-all state across any value rewrite it performs. A second
 * `select()` after focus guards against browsers that reset selection
 * on programmatic focus.
 */
function focusWithFromHint(el: HTMLElement, from: FocusFrom) {
  const isStdInput = el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement;
  if (from !== 'click' && isStdInput) {
    try {
      el.setSelectionRange(0, el.value.length);
    } catch {
      /* input types without text selection (e.g. number) throw — ignore */
    }
  }
  el.focus();
  if (from !== 'click' && isStdInput) {
    try {
      el.select();
    } catch {
      /* ignore */
    }
  }
}

type FocusSlot = {
  first?: HTMLElement;
  last?: HTMLElement;
  fns: { first?: FocusRef; last?: FocusRef };
};
const focusRefCache = new WeakMap<EditCellRegisterFocus, FocusSlot>();

function composeAndRegister(slot: FocusSlot, reg: EditCellRegisterFocus) {
  if (!slot.first && !slot.last) {
    reg(null);
    return;
  }
  reg((from) => {
    const fromAfter = from === 'bottom' || from === 'tab-back';
    const target = fromAfter ? (slot.last ?? slot.first) : (slot.first ?? slot.last);
    if (!target) return;
    focusWithFromHint(target, from);
  });
}

/**
 * Helper for binding an input to a cell's focus registry via a
 * function-template-ref. Call once per input and tag each with `who`
 * when the cell hosts multiple inputs:
 * ```
 * :ref="defaultEditCellFocusRef(registerFocus)"             // single input
 * :ref="defaultEditCellFocusRef(registerFocus, 'first')"    // multi-input, first
 * :ref="defaultEditCellFocusRef(registerFocus, 'last')"     // multi-input, last
 * ```
 * The helper composes one focus-handler per cell that routes `'top'`/
 * `'click'` to the first input and `'bottom'` to the last, selecting the
 * value on keyboard entry. Stable ref identity across renders.
 */
export const defaultEditCellFocusRef = (
  registerFocus: EditCellRegisterFocus,
  who: 'first' | 'last' = 'first'
): FocusRef => {
  let slot = focusRefCache.get(registerFocus);
  if (!slot) {
    slot = { fns: {} };
    focusRefCache.set(registerFocus, slot);
  }
  const cached = slot.fns[who];
  if (cached) return cached;
  const fn: FocusRef = (inst) => {
    const el = resolveInputElement(inst);
    if (el === slot[who]) return;
    if (el) slot[who] = el;
    else delete slot[who];
    composeAndRegister(slot, registerFocus);
  };
  slot.fns[who] = fn;
  return fn;
};

/**
 * Unified keydown handler for editCell inputs. Detects whether the
 * native key would move the caret inside the input; if it would leave
 * the input (edge reached, Tab, commit Enter, …) the matching
 * `focusAbove`/`focusBelow`/`focusLeft`/`focusRight` is invoked.
 *
 * Wire on the input inside the editCell slot:
 * ```
 * @keydown="(e) => onEditCellKeydown(e, {
 *   focusAbove, focusBelow, focusLeft, focusRight, inputType: 'text',
 * })"
 * ```
 *
 * Behavior per `inputType`:
 * - `'text'`      ← / → edge-detect (caret at start/end); ↑/↓ always commit;
 *                 Enter commits right; Tab moves in grid order and is left
 *                 native when the grid reports no internal/external target;
 * - `'multiline'` ← / → edge-detect; ↑ commit when caret in first line, ↓
 *                 in last line (otherwise native); Enter native; Ctrl+Enter
 *                 commits right;
 * - `'select'`    ↑/↓ move rows while the list is closed (an open list consumes
 *                 them with preventDefault — pair with `arrow-keys-navigate`
 *                 on the select so the closed field leaves them alone); ← / →
 *                 always commit; Enter commits right;
 * - `'wysiwyg'`   all arrows native; Enter native; Ctrl+Enter commits
 *                 right;
 *
 * Skipped when the event is already `defaultPrevented` (e.g. open dropdown
 * that already consumed the key) — except Tab. Autocomplete commits the pick
 * with `preventDefault` so the browser does not walk trailing action icons;
 * the cell must still move (Menge → Einheit → Preis, …).
 */
export const onEditCellKeydown = (
  event: KeyboardEvent,
  ctx: {
    focusAbove: () => void;
    focusBelow: () => void;
    focusLeft: (intent?: CellNavigationIntent) => boolean | void;
    focusRight: (intent?: CellNavigationIntent) => boolean | void;
    inputType: EditCellInputType;
  }
): void => {
  if (event.altKey) return;
  if (event.defaultPrevented && event.key !== 'Tab') return;

  const target = event.target;
  const isStdEditable = target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement;

  const selStart = isStdEditable ? (target.selectionStart ?? 0) : 0;
  const selEnd = isStdEditable ? (target.selectionEnd ?? 0) : 0;
  const valueLen = isStdEditable ? target.value.length : 0;

  const atLeftEdge = !isStdEditable || selStart === 0;
  const atRightEdge = !isStdEditable || selEnd === valueLen;
  const atFirstLine = !isStdEditable || !target.value.substring(0, selStart).includes('\n');
  const atLastLine = !isStdEditable || !target.value.substring(selEnd).includes('\n');
  const allSelected = atLeftEdge && atRightEdge && valueLen > 0;

  const t = ctx.inputType;
  const ctrlLike = event.ctrlKey || event.metaKey;
  // Commits the editor's value now, while it still owns focus. The browser
  // does not count this synthetic change: when the focus then leaves the editor
  // (a native Tab, a page stop focused by the grid, an arrow move that blurs it
  // before the cell goes inert) it fires its own change for the same value, and
  // the cell would commit twice — two identical PATCHes (I-3485). That repeat is
  // swallowed.
  const commitPendingInput = () => {
    if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
      event.target.dispatchEvent(new Event('change', { bubbles: true }));
      swallowRepeatedNativeChange(event.target);
    }
  };

  switch (event.key) {
    case 'Tab': {
      // `v-model.lazy` editors commit on `change`. Moving the table cursor first
      // makes a lazy grid inert/unmount this input before the browser's later
      // blur can emit that change, so the typed value silently reverts. Commit
      // while this editor still owns focus, then let table navigation move it.
      commitPendingInput();
      const handled = event.shiftKey ? ctx.focusLeft('tab') : ctx.focusRight('tab');
      // A table boundary without an explicit page target stays native. The
      // browser's change on the following blur repeats the commit above and is
      // swallowed (`commitPendingInput`).
      if (handled === false) return;
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    case 'Enter': {
      if (ctrlLike) {
        event.preventDefault();
        event.stopPropagation();
        commitPendingInput();
        ctx.focusRight();
        return;
      }
      if (t === 'multiline' || t === 'wysiwyg') return;
      event.preventDefault();
      event.stopPropagation();
      commitPendingInput();
      ctx.focusRight();
      return;
    }
    case 'ArrowUp': {
      if (ctrlLike || event.shiftKey) return;
      if (t === 'wysiwyg') return;
      if (t === 'multiline' && !atFirstLine) return;
      event.preventDefault();
      event.stopPropagation();
      commitPendingInput();
      ctx.focusAbove();
      return;
    }
    case 'ArrowDown': {
      if (ctrlLike || event.shiftKey) return;
      if (t === 'wysiwyg') return;
      if (t === 'multiline' && !atLastLine) return;
      event.preventDefault();
      event.stopPropagation();
      commitPendingInput();
      ctx.focusBelow();
      return;
    }
    case 'ArrowLeft': {
      if (ctrlLike || event.shiftKey) return;
      if (t === 'wysiwyg') return;
      if (!atLeftEdge || allSelected) return;
      event.preventDefault();
      event.stopPropagation();
      commitPendingInput();
      ctx.focusLeft();
      return;
    }
    case 'ArrowRight': {
      if (ctrlLike || event.shiftKey) return;
      if (t === 'wysiwyg') return;
      if (!atRightEdge || allSelected) return;
      event.preventDefault();
      event.stopPropagation();
      commitPendingInput();
      ctx.focusRight();
      return;
    }
  }
};

/**
 * Drop the ONE `change` the browser fires when an editor that was just
 * committed by `onEditCellKeydown` loses focus, as long as its value is still
 * the committed one. Listens in the capture phase on the
 * document so the editor's own listeners (`v-model.lazy`, a component's
 * `@change`) never see it. Ends at the first such change, at new input in the
 * editor, or just after the blur — a later, real edit commits as usual.
 */
function swallowRepeatedNativeChange(target: EventTarget | null): void {
  if (!(target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement)) return;
  const editor = target;
  const doc = editor.ownerDocument;
  const committedValue = editor.value;
  const stop = () => {
    doc.removeEventListener('change', swallow, true);
    editor.removeEventListener('input', stop);
    editor.removeEventListener('blur', stopAfterBlur);
  };
  const stopAfterBlur = () => setTimeout(stop, 0);
  const swallow = (event: Event) => {
    if (event.target !== editor) return;
    stop();
    if (editor.value !== committedValue) return;
    event.stopImmediatePropagation();
  };
  doc.addEventListener('change', swallow, true);
  editor.addEventListener('input', stop);
  editor.addEventListener('blur', stopAfterBlur);
}

export const analyzeSelection = (
  target: EventTarget
): {
  atLeftEdge: boolean;
  atRightEdge: boolean;
  atFirstLine: boolean;
  atLastLine: boolean;
  allSelected: boolean;
} => {
  const isStdEditable = target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement;

  const selStart = isStdEditable ? (target.selectionStart ?? 0) : 0;
  const selEnd = isStdEditable ? (target.selectionEnd ?? 0) : 0;
  const valueLen = isStdEditable ? target.value.length : 0;

  const atLeftEdge = !isStdEditable || selStart === 0;
  const atRightEdge = !isStdEditable || selEnd === valueLen;
  const atFirstLine = !isStdEditable || !target.value.substring(0, selStart).includes('\n');
  const atLastLine = !isStdEditable || !target.value.substring(selEnd).includes('\n');
  const allSelected = atLeftEdge && atRightEdge && valueLen > 0;

  return {
    atLeftEdge,
    atRightEdge,
    atFirstLine,
    atLastLine,
    allSelected,
  };
};

/**
 * The fields a mounted column may re-publish to its table. The registration
 * itself is a snapshot taken when the column mounts, so anything that arrives
 * later — the layout state the table writes back, the filter options and labels
 * a grid loads with its schema — reaches the table through here.
 */
export type ColumnRegistryUpdate<
  T extends Record<string, unknown>,
  Column extends keyof T | (string & {}) = keyof T | (string & {}),
  T_keyof_T = T[keyof T],
> = Pick<TableColumnConfig<T, Column, T_keyof_T>, 'columnId'> &
  Partial<
    Pick<
      TableColumnConfig<T, Column, T_keyof_T>,
      'layout' | 'align' | 'displayName' | 'filterOptions' | 'filterOptionLabel'
    >
  >;

export const registerColumKey = Symbol('registerColumKey') as InjectionKey<{
  registerColum: (column: TableColumnConfig<Record<string, unknown>>) => () => void;
  updateRegisterValue: (column: ColumnRegistryUpdate<Record<string, unknown>>) => () => void;
}>;

export interface TableState<T extends Record<string, unknown>> {
  filterModifier: FilterModifier;
  filterValues: Map<TableColumnConfig<T, string>['columnId'], (T[keyof T] | string)[]>;
  /** most recent first */
  sortOrder: {
    columnId: TableColumnConfig<T, string>['columnId'];
    order: SortOrder;
  }[];
  pagination: PaginationConfig;
  visibleColumns: string[];
}

export type NxTablePaginationMode = 'infinite' | 'pages';

export interface PaginationConfig {
  offset: number;
  limit: number;
}

/**
 * Row height step of the table (I-2176): `normal` is the kit default; a grid
 * whose spec asks for more air (the attendance review's month table, §4.2) sets
 * `airy` itself.
 */
export type NxTableRowDensity = 'airy' | 'normal' | 'compact';

/** Body row height per density step, em-based so rows track the table font. */
export const ROW_DENSITY_HEIGHTS: Record<NxTableRowDensity, string> = {
  // em at the 14px table step: 56 / 44 / 32 px, far enough apart to be seen.
  airy: 'calc(3.5em / 0.875)',
  normal: 'calc(2.75em / 0.875)',
  compact: 'calc(2em / 0.875)',
};

export type TableMetadataState<T extends Record<string, unknown>> = {
  filterModifier: FilterModifier;
  pageSize: number;
  columns: ColumnMetadataState<T>[];
  filterValues: Map<TableColumnConfig<T, string>['columnId'], (T[keyof T] | string)[]>;
  /** External pre-filters (e.g. user table should only show users known to company). Always AND-combined at root; user filters are nested in their own modifier group. */
  baseFilterValues: Map<TableColumnConfig<T, string>['columnId'], (T[keyof T] | string)[]>;
  /**
   * Operator-based conditions per column (from the header filter popover).
   * Combines with the column's quick `filterValues` entry via the column
   * filter's own modifier; across columns via `filterModifier`.
   */
  columnFilters: Map<TableColumnConfig<T, string>['columnId'], NxTableColumnFilter>;
  /** Nested and/or condition tree from the filter wizard; AND-combined at the root. `null` when unset. */
  advancedFilter: NxTableFilterGroup | null;
  pagination: PaginationConfig;
  /**
   * Shows or hides the quick-filter row ("Suchleiste") of this table instance
   * (`setTableState` only, never stored), e.g. after putting a filter into it.
   * The user's preference (`table.search_row`, see `tableSettings.ts`) applies
   * again once they change it in the table's settings.
   */
  searchRowVisible?: boolean;
  /**
   * The table's own row height step, like the `rowDensity` prop (a grid
   * catalog may carry one). The user's `table.row_density` wins when set.
   */
  rowDensity?: NxTableRowDensity;
};

export type BaseFilterMode = 'lock' | 'merge';

export type ColumnMetadataState<T extends Record<string, unknown>> = {
  columnId: keyof T | (string & {});
  visible: boolean;
  /** Explicit pixels; omitted while a `columnGrow` column is sized automatically. */
  width?: number;
  position: number;
  sortOrder: SortOrder | null;
  /** 0 is the highest prio, then 1, 2, ... */
  sortPrio: number | null;
  /** Pinned to the left edge while the grid scrolls sideways (column dialog). */
  sticky?: boolean;
};

// ---- NxTable component types ----

/**
 * Per-row visual overrides returned by {@link NxTableProps.rowStyle}. Every field
 * is optional; an omitted field (or an `undefined` return) keeps the table
 * default. Pass colors as tokens (`var(--nx-…)`), never hex, so light/dark themes
 * resolve automatically. The object is intentionally open for extension — add
 * further optional keys (e.g. text color) as needs arise.
 */
export type RowStyle = {
  /**
   * Row background. Replaces the base/zebra fill. Hover, selection, cursor and
   * the active row still override it, so those highlights stay visible; only the
   * zebra stripe is suppressed for the styled row.
   */
  bgColor?: string;
  /**
   * Row font weight, e.g. a sum row a step heavier. Pass a weight token
   * (`var(--nxt-font-weight-medium)`), never a number. The row's text inherits
   * it, plain-cell edit fields included; content with a weight of its own (a
   * badge, a bold figure) keeps that. Header, summary and the other rows keep
   * their weight.
   */
  fontWeight?: string;
  /**
   * Per-side border thickness in px; the color is fixed to
   * `var(--nx-table--border)`. `bottom` overrides the default 1px row separator,
   * `top` adds a top border (default none). Omitting a side keeps its default.
   */
  border?: { top?: number; bottom?: number };
};

/**
 * Semantic CSS classes for a rendered body row. Use this instead of encoding
 * state in `rowStyle` when a consumer's stylesheet owns the visual treatment.
 */
export type RowClass = string | string[] | Record<string, boolean>;

/**
 * Selection interaction model. `'cells'` preserves the established grid
 * behavior; `'rows'` makes selection work as a list, including on read-only
 * tables, without requiring `editMode`. `'checkbox'` separates the active row
 * from the selection: a click or an arrow key only moves the active row, rows
 * are marked for bulk actions with a checkbox column (see
 * `NxTableProps.rowSelectionMode`).
 */
export type NxTableRowSelectionMode = 'cells' | 'rows' | 'checkbox';

/**
 * Intent colour of a group header's dot — for a colour that says something
 * (overdue = `danger`). `neutral` (the default) is a muted grey.
 */
export type NxTableGroupTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger';

/** One row group of a grouped table (`NxTableProps.groupBy`). */
export type NxTableGroup = {
  /** The key `groupBy` returns for the rows of this group. */
  key: string;
  /** Title of the group's header row. */
  label: string;
  /** Intent colour of the header dot. Wins over `hue` when both are set. */
  tone?: NxTableGroupTone;
  /** Category hue of the header dot, for a colour that only tells groups apart. */
  hue?: NxHue;
};

/** Props of NxTable's `#group-header` slot. */
export type NxTableGroupHeaderSlotProps<T> = {
  /** The group; the implicit group has the key `UNGROUPED_GROUP_KEY`. */
  group: NxTableGroup;
  /** Number of rows in the group. */
  count: number;
  /** The group's rows, in display order. */
  rows: T[];
  /** True for the implicit group of rows without a listed key. */
  ungrouped: boolean;
};

/** Props of NxTable's `#row-detail` slot (`expandableRows`). */
export type NxTableRowDetailSlotProps<T> = {
  /** The expanded row. */
  row: T;
  /** Its id (`rowIdKey`). */
  rowId: string;
  /** Its index in `data`. */
  rowIndex: number;
  /** Closes the detail, e.g. from a close button inside it. */
  collapse: () => void;
};

/** Payload sent by `visible-range-change` for the current virtual row window. */
export type NxTableVisibleRange = {
  startIndex: number;
  endIndex: number;
};

/**
 * Consumer-owned native row drag hook. Return `false` to cancel the browser
 * drag. NxTable never writes to `event.dataTransfer` for this hook.
 */
export type NxTableRowDragStart<T extends Record<string, unknown>> = (
  rows: { id: string; data: T }[],
  event: DragEvent
) => void | false;

/**
 * Save status of one edit cell. Pending/error feed the cell's input wrapper;
 * success stays on the table cell itself so it survives lazy-editor unmounts.
 */
export type CellSaveStatus = {
  loading: boolean;
  error: boolean;
  /** Shown in the tooltip on the error icon. */
  errorMessage?: string;
};

export type NxTableProps<T extends Record<string, unknown>> = {
  /**
   * The data to display. Owned by the consumer. NxTable does not mutate this array.
   * When `onTableStateChange` is invoked, the consumer is responsible for updating this
   * prop before resolving the returned Promise.
   */
  data: T[];
  /** Show one column-bound, read-only summary below the complete data body. Off by default. */
  summary?: boolean;
  summaryValues?: Record<string, NxTableSummaryValue>;
  /** Keyed native summary overrides, merged after registered column defaults. Undefined fields keep those defaults. */
  columnSummaries?: Readonly<Record<string, NxTableSummaryColumn<T>>>;
  /**
   * The row's caption (default „Zusammenfassung“ / „Summary“). It belongs to the
   * row, not to a column: it starts where the data columns start and may run over
   * the leading summary cells that stay empty. Without room for all of it (or with
   * a total in the first column) it collapses to „Σ“ in the indicator gutter, the
   * caption as its tooltip.
   */
  summaryLabel?: string;
  /** A data-owner completeness claim; placeholders and holes still invalidate aggregation. */
  summaryDataComplete?: boolean;
  summaryLoading?: boolean;
  summaryError?: boolean;
  /**
   * Total number of rows on the server (across all pages). Used to compute whether the
   * last page has been reached. If omitted, NxTable falls back to detecting end-of-data
   * by observing that a pagination call did not grow `data.length`.
   */
  totalCount?: number;
  /**
   * Called when a filter, sort or pagination changes. The consumer must update the
   * `data` (and `totalCount`) prop before resolving the returned Promise. NxTable awaits
   * this Promise to drive its loading indicator and pagination-end detection.
   */
  onTableStateChange?: (config: TableMetadataState<T>) => Promise<void>;

  /** Function that returns a value to identify each row, e.g. a ID or UUID. */
  rowIdKey: (value: T) => string;
  /**
   * Returns the row id (as produced by `rowIdKey`) of the row's parent, or
   * `null` for top-level rows. Trees may be arbitrarily deep; `data` must be
   * in pre-order (every row directly followed by its subtree). When provided,
   * row drag & drop becomes tree-aware: a row drags together with its whole
   * subtree and reorders only among its direct siblings — it never changes
   * its parent.
   */
  rowParentId?: (row: T) => string | null;
  /**
   * Called when the user requests a CSV export that needs more rows than `data` currently
   * holds (e.g. "export full result set"). Returns the rows to export. Does not affect
   * `data`. If not provided, CSV export uses only the rows currently in `data`.
   */
  onExportData?: (config: TableMetadataState<T>) => Promise<T[]>;

  /**
   * Called when a new row gets created (onSave in creationMode or in `editMode` when the user presses `ArrowDown` in last column).
   * Returns the new index of the created row, so the table can focus the new cell. If defined, row creation is allowed.
   *
   * `undefined` means the write was REFUSED and the consumer has already told
   * the user why (I-1935). It must not reject for that case: a rejected create
   * from a click handler travels up as an unhandled rejection, and a host that
   * contains render errors — `@blpde/nx-layout`'s `LayoutComponentSlot` — takes
   * its whole section off the screen for the rest of the page's life. The table
   * simply leaves the create row standing so the user can correct it.
   */
  onCreate?: (rowToCreate: PartialOrNull<T>) => Promise<number | undefined>;
  /**
   * Factory for ArrowDown-past-last-row create. Lets a consumer seed the
   * POST body (e.g. sticky line type) and name the column that should
   * receive edit-focus after the row lands. Omitted → empty `{}` and the
   * first focusable column.
   */
  createFromArrowNav?: () => {
    row?: PartialOrNull<T>;
    focusColumnId?: string;
  };
  /**
   * Whether NAVIGATION may create a row at all — ArrowDown past the last row,
   * and Tab past the last editor. Default `true`, which is the behaviour every
   * grid had before this existed.
   *
   * ⚠️ Set it to `false` when a row CANNOT be seeded from the grid. `onCreate`
   * is the only gate otherwise, and a consumer that needs `doCreation` (its own
   * dialog collects a required FK first) has to pass `onCreate` — which hands
   * ArrowDown an empty `{}` POST as a side effect. On a link table whose FK is
   * nullable that write SUCCEEDS and leaves a row pointing at nobody
   * (BLP/nx#1769: a customer's Ansprechpartner link with `contact_id` null,
   * every joined cell blank, and the reactor handing that blank row the
   * Hauptkontakt).
   */
  allowNavigationCreate?: boolean;
  /**
   * Opt-in factory for Tab-past-the-last-editor creation. When provided, Tab
   * appends a row and continues in `focusColumnId` instead of leaving the grid.
   * Omitted tables keep the normal page-level Tab boundary.
   */
  createFromTabNav?: () => {
    row?: PartialOrNull<T>;
    focusColumnId?: string;
  };
  /** Called when a cell gets edited (i.e. when `onEdit` is called on any editCell). */
  onEdit?: (rowToEdit: { id: string; index: number }, newValues: Partial<T>) => Promise<void>;
  /**
   * Save status of a single edit cell. The table shows a delayed spinner while
   * saving and keeps the error icon/tooltip on failure. Returning `undefined`
   * means idle. A successful save is not painted on the cell — the consumer calls
   * `showSaved()`, which flashes one check in the table's top-right corner.
   *
   * Deliberately a lookup rather than one status for the table: a shared state
   * turns one failed PATCH into a table-wide red. The table stays dumb here; only
   * the consumer knows how its writes went (see `DataTable.onEdit`).
   *
   * Cells opt in as a group: pass this and every editable cell gets its own
   * wrapper. Omit it and nothing is wrapped at all.
   *
   * One cost to know about before opting in (accepted for I-958, see its review):
   * every edit cell then reserves the input's status slot (`1.25em`) for good,
   * because `NxInputWrapper` renders it whenever a wrapper is above it, not only
   * while saving. Narrow number columns lose that much text width at rest.
   *
   * Nothing in `NxTable`'s own render effect reads the lookup: the cell and row
   * state is written by directives, and the wrapper props are evaluated inside
   * `NxInputActionsAlignProvider`'s slot, i.e. during that child's render. A
   * status change therefore invalidates only what shows that status — the visible
   * body is not rendered again (I-995; it used to cost four full body renders per
   * save). Consequence for the consumer: the store must be reactive per cell key,
   * the way `useCellSaveStatus` mutates `entries.value[key]`. Replacing the whole
   * store object on every write re-runs every cell's effect instead of one.
   */
  cellSaveStatus?: (rowId: string, columnId: string) => CellSaveStatus | undefined;
  /**
   * Called when a row gets deleted. If defined, row deletion is allowed.
   * Return `false` for a handled failure: the table then keeps its pending row,
   * cursor, and selection unchanged. `void` remains a successful result for
   * backwards compatibility.
   */
  onDelete?: (rowToDelete: { id: string; index: number }) => Promise<boolean | void>;
  /**
   * Asked BEFORE the delete confirmation opens, with the rows the delete would
   * remove (the clicked row, or the whole selection). A non-empty string refuses
   * up front: no confirmation is shown and `delete-refused` carries the reason.
   * For a refusal the screen can tell from the row alone — asking „Löschen?"
   * first and failing after the confirmation reads as a broken delete.
   */
  deleteGuard?: (rows: T[]) => string | undefined;
  /**
   * Which rows offer deletion when `onDelete` is set; a row it answers `false`
   * for shows no X, is left out of the Delete key and the context menu's
   * delete. `index` is the row's position in `data`. Default: every row.
   */
  rowDeletable?: (row: T, index: number) => boolean;

  /**
   * Emitted whenever a user moves a row. If defined, row movement (drag/drop) is allowed.
   * `fromPosition`/`toPosition` refer to the first row of the moved unit (`toPosition` =
   * its final index). With `rowParentId` set, `movedCount` carries the size of the moved
   * subtree (the row plus all of its descendants) and the consumer must move that whole
   * slice; without `rowParentId` the callback is invoked with exactly two arguments as
   * before.
   */
  onRowMoved?: (fromPosition: number, toPosition: number, movedCount?: number) => Promise<void>;
  /**
   * Which rows can be moved when `onRowMoved` is set; a row it answers `false`
   * for shows no drag affordance on its handle (no grab cursor, not
   * draggable), has no „An Position verschieben“ in its row menu, and a move
   * of it from any entry never reaches `onRowMoved`. It may still be a drop
   * target for the rows that move. `index` is the row's position in `data`.
   * Default: every row.
   */
  rowMovable?: (row: T, index: number) => boolean;
  /**
   * Starts a consumer-owned native row drag. It receives all selected rows
   * when the dragged row is selected, otherwise only that row. Returning
   * `false` cancels the drag. This is deliberately separate from
   * `onRowMoved`: when present, NxTable does not run its internal reorder
   * drag/drop behavior and leaves `dataTransfer` entirely to the consumer.
   */
  onRowDragStart?: NxTableRowDragStart<T>;
  /** Completes a consumer-owned native drag, including cancellation. */
  onRowDragEnd?: (event: DragEvent) => void;
  /** Consumer-owned native drop target, independent from row reordering. */
  canDropRow?: (event: DragEvent, row: T) => boolean;
  /** Called only after canDropRow accepts the final drop. */
  onRowDrop?: (event: DragEvent, row: T) => void;
  /** Accessible feedback while the native drag is over an accepted row. */
  rowDropLabel?: (row: T) => string;

  paginationLimit: number;
  /** number of remaining items (to scroll to), when the pagination should be triggered. Defaults to `3`. */
  paginationTriggerRemainingItemsCount?: number;
  /**
   * How further pages are requested. `infinite` (default) appends pages while
   * the user scrolls towards the end. `pages` turns the scroll trigger off and
   * renders a footer pager (prev / next, "page x of y", optional page-size
   * select); every page change goes through the same
   * `onTableStateChange({ pagination: { limit, offset } })` call, and `data`
   * is expected to hold just the requested page. Provide `totalCount` so the
   * pager can show the page total; without it "next" stays enabled while
   * pages come back full.
   */
  paginationMode?: NxTablePaginationMode;
  /**
   * Page sizes offered by the footer pager's select (`paginationMode="pages"`
   * only). Omit to hide the select. Picking a size resets to the first page.
   */
  paginationPageSizes?: number[];

  /**
   * Will show the editCell- instead of cell-definitions.
   * If `true`, all columns need a editCell definition. Defaults to `false`
   */
  editMode?: boolean;
  /**
   * When true (with editMode), only the **active row** (cursor row) mounts heavy
   * `editCell` widgets (relation pickers with lookup browser, editors, …).
   * Other rows stay on lightweight display cells until clicked. Defaults to `false`.
   *
   * Row-scoped (not cell-scoped) so Einheit/MWST/etc. keep their full picker UI
   * while editing a line — cell-scoped lazy hid the more-button until focus.
   */
  lazyEditCells?: boolean;
  /**
   * When true for a row, the body paints a single lightweight shell instead of
   * per-column cells (windowed/sparse placeholders). Defaults to never.
   */
  isPlaceholderRow?: (row: T) => boolean;
  /**
   * Whether a loaded row can be marked for selection. Defaults to true;
   * placeholders are never selectable. Applies to checkboxes, modifier and
   * keyboard selection, drag ranges and the selection API. Ineligible rows
   * stay visible and can still become active, but their checkbox is disabled
   * and they do not count toward the header's select-all state. A selection
   * is pruned when a row becomes ineligible.
   */
  isRowSelectable?: (row: T) => boolean;
  /**
   * Enable the established row (multi-)selection controls. In legacy
   * `'cells'` mode this retains the edit-grid interaction model. Prefer
   * `rowSelectionMode: 'rows'` for a read-only/list table; it enables the
   * same selection state without requiring this flag.
   */
  allowRowSelection?: boolean;
  /**
   * Opt into row-first selection for list/read-only consumers. `'cells'`
   * (default) preserves the existing grid behavior. `'rows'` enables row
   * selection outside `editMode`: click, modifier/range click, arrows and
   * Ctrl/Cmd+A operate on rows, and `active-row-change` tracks the focused
   * body row.
   *
   * `'checkbox'` keeps the ACTIVE row and the SELECTION apart (a mail-style
   * list: the click opens a row, the checkboxes collect rows for a bulk bar).
   * A checkbox column follows the indicator column, with a header box that
   * selects all rows of `data` (mixed while some are selected) or clears them.
   * A plain click or an arrow key only moves the active row
   * (`active-row-change`); the checkbox toggles its row, Shift+click on a
   * checkbox sets the range from the previous one to the clicked box's new
   * state, Space (Shift+Space: range) toggles the active row, Ctrl/Cmd+click and
   * Shift+click on a row act like on its box, Escape clears the selection,
   * Ctrl/Cmd+A selects all.
   * Selection state, API and `selection-change` are the same as in the other
   * modes, so `NxSelectionBar` binds to it unchanged.
   */
  rowSelectionMode?: NxTableRowSelectionMode;
  /**
   * Mark rows by dragging: press the mouse button on a row and drag up or down —
   * every row from the start row to the one under the pointer is marked, no
   * Shift needed. Dragging back shrinks the range, Ctrl/Cmd held at the start
   * adds the range to the rows marked before, Escape during the drag restores
   * the selection from before it. Near the top or bottom edge the table
   * scrolls on its own. A plain click keeps its behaviour; the drag starts
   * once the pointer reaches another row. Text inputs, buttons and links keep
   * their own mouse gesture, as do the reorder handle (`onRowMoved`) and a
   * native row drag (`onRowDragStart`) that starts on a marked row. The text
   * of display cells can no longer be selected with the mouse.
   *
   * A special case, not a default for lists: the mail list and the ERP grids
   * keep Shift and Ctrl/Cmd for ranges, switch it on only where a screen
   * explicitly asks for it.
   *
   * Needs row selection (`allowRowSelection` or `rowSelectionMode` `'rows'` /
   * `'checkbox'`); the result arrives through the usual selection state and
   * `selection-change`, and once more as `drag-selection-end` when the button
   * comes up. Defaults to `false`.
   */
  dragSelection?: boolean;
  /**
   * Rows open a detail below themselves: a chevron toggle per row sits in the
   * indicator column at the table's edge (in place of the cursor caret; the
   * active row still shows by its fill and edge marker), and an open row renders the
   * `#row-detail` slot (`NxTableRowDetailSlotProps`) directly underneath, as
   * wide as the visible table. A function decides per row whether it can open
   * (rows it rejects show no toggle). Several rows may be open at once; the
   * toggle is reached with the arrow keys like the other chrome columns and
   * opens or closes with Enter or Space. Open rows are reported by
   * `row-expanded-change` and steered from outside through `setRowExpanded`,
   * `toggleRowExpanded` and `collapseAllRows`; a row that leaves `data` closes.
   * Defaults to `false`.
   */
  expandableRows?: boolean | ((row: T) => boolean);
  /**
   * Accessible name of a row's checkbox (`rowSelectionMode: 'checkbox'`), which
   * has to say WHICH row it selects — the header says what. `rowIndex` is the
   * row's index in `data`. Defaults to the table's „Zeile n auswählen“.
   */
  rowCheckboxLabel?: (row: T, rowIndex: number) => string;
  /** Accessible name of the header checkbox. Defaults to the table's „Alle auswählen“. */
  selectAllLabel?: string;

  /**
   * Opt-in row grouping: the group key of a row. Rows are shown in blocks, each
   * behind a header row (coloured dot, title, count, a rule line) in the order of
   * `groups`. Within a group the rows keep the order of `data`, so a sort or a
   * filter applied to `data` works inside each group; a group without rows is
   * not shown (`showEmptyGroups`). Header rows are plain display rows: never
   * selected, edited, counted or exported, and keyboard navigation skips them.
   *
   * Indices stay those of `data` wherever the table hands one out (`rowIndex` of
   * the cell slots and `toCsvString`, `onEdit`/`onDelete`, `scrollTo`,
   * `focusedCell.rowIdx`); only `virtualWindow` and `visible-range-change` count
   * display positions, so windowed/sparse loading (`isPlaceholderRow`) does not
   * mix with grouping — placeholder rows are listed after all groups. The CSV
   * export follows the display order.
   *
   * With `rowParentId` a subtree stays with its top-level row, whose key decides
   * the group. Grouping is switched off while `onRowMoved` is set: reordering
   * needs the rows in the order of `data` (dev warning).
   */
  groupBy?: (row: T) => string | null | undefined;
  /**
   * The groups in display order. Rows whose key is `null`/`undefined` or not
   * listed land in an implicit last group (`ungroupedLabel`). Without `groups`
   * every distinct key forms a group, in order of first appearance and titled
   * with the key itself.
   */
  groups?: NxTableGroup[];
  /** Title of the implicit group. Defaults to „Ohne Gruppe“. */
  ungroupedLabel?: string;
  /** Show the header of a listed group that has no rows. Defaults to `false`. */
  showEmptyGroups?: boolean;
  /** Accessible text of a group header's count. Defaults to „n Einträge“. */
  groupCountLabel?: (count: number) => string;
  /**
   * Opt-in page-scroll priority: while focus is OUTSIDE the table, vertical
   * wheel/trackpad over the grid scrolls the surrounding page — hover is not
   * enough. The grid claims vertical scroll after a real interaction (click /
   * scrollbar) and keeps it at the vertical edges until focus leaves the grid.
   * Horizontal intent (shift+wheel / dominant deltaX) still scrolls a wide
   * grid. Defaults to `false`.
   */
  wheelRequiresFocus?: boolean;
  /**
   * What happens when a vertical wheel reaches the table's end. `auto` (the
   * default) lets it chain on to the page, which list pages want; `contain`
   * stops it there — a working surface such as a document's line grid must not
   * fling the page when its last row is reached.
   */
  overscrollY?: 'auto' | 'contain';

  /** Prefix for the csv-filename when the user exports the table-data. If not given, the export csv functionality will be disabled */
  csvFilenamePrefix?: string;
  /** Sort order that is used if the sort gets toggled from off to on */
  firstSortOrder?: SortOrder;
  /** Max rows to render before the actual row height has been measured. Prevents rendering all rows at once on first paint. Defaults to `50`. */
  initialRenderLimit?: number;

  /** Wether to render the create button on the top right. Only applicable if allowed action contains 'create'. Defaults to `false` */
  hideCreateButton?: boolean;
  /** Defaults to 80 (px) */
  minColWidth?: number;
  /** Defaults to 800 (px) */
  maxColWidth?: number;
  /**
   * Relative shares of spare row width, keyed by column id. Positive, finite
   * weights grow visible, unpinned columns from their minimum widths; explicit
   * stored widths take precedence. The first manual resize freezes the visible
   * automatic columns so neighbouring boundaries stay still. Resetting widths
   * restores growth. Omitted columns stay content-sized. Narrow viewports scroll
   * instead of squeezing the minimums. Ignored for `layout="list"`.
   */
  columnGrow?: Record<string, number>;
  /** Defaults to `40px` */
  headerRowHeight?: string;
  /**
   * Explicit body row height; when set it wins over `rowDensity`. Unset, the
   * height comes from the density step (`ROW_DENSITY_HEIGHTS`). em-based
   * values track the table font and scale.
   */
  bodyRowHeight?: string;
  /**
   * Row height step (I-2176): `'airy'` (48px at the 14px table step),
   * `'normal'` (default, 40px) or `'compact'` (36px) — the table's own. A row
   * density the user picked in the table settings (`table.row_density`, one
   * for every table) wins. Ignored when `bodyRowHeight` is set explicitly.
   */
  rowDensity?: NxTableRowDensity;
  /**
   * Zebra stripes of THIS table, fixed by the consumer — for a business rule
   * such as a grid that must never stripe. `true` or `false` wins over the
   * user's table settings (`table.zebra`, one for every table); unset (the
   * default) the setting decides.
   */
  zebra?: boolean;
  /**
   * Row-height contract of the body.
   * - `'auto'` (default): rows grow with their content (`min-height: bodyRowHeight`).
   * - `'uniform'`: every row is EXACTLY `bodyRowHeight` tall — display and edit
   *   alike. Cells clip their overflow, and the virtualizer stops measuring
   *   (offset = index × rowHeight, no ResizeObserver, no scroll compensation),
   *   so a lazy-edit swap, a save status, or scrolling can never shift rows.
   *   The exact virtual row size is measured once from a reference row, so
   *   `bodyRowHeight` may be em/token-based here too.
   */
  rowSizing?: 'auto' | 'uniform';
  /**
   * Columns pinned to the left edge BY DEFAULT, next to the indicator column,
   * while the grid scrolls horizontally (e.g. the position number). The user
   * overrides it per column in the column dialog (`sticky` in the table state);
   * an explicit `false` there unpins a default. Only the LEADING run of the
   * visible order is pinned: a pinned column dragged behind an unpinned one
   * scrolls normally, so pinned cells never overlap each other. The row-action
   * (delete) column is always pinned right and is not part of this.
   */
  stickyColumnIds?: string[];

  /**
   * External pre-filters (e.g. user table should only show users known to company). Always AND-combined at the root.
   * User-column-filters are nested in their own modifier group, so `and/or` of user filters never clashes with base filters.
   */
  baseFilter?: Map<TableColumnConfig<T, string>['columnId'], (T[keyof T] | string)[]>;
  /**
   * How base filters interact with the column's filter UI.
   * - `merge` (default): UI stays editable; server receives both sets AND-combined.
   * - `lock`: UI is disabled for columns present in `baseFilter`; user cannot set a conflicting filter.
   */
  baseFilterMode?: BaseFilterMode;

  /**
   * Optional per-row style override. Called for every rendered row; return
   * `undefined` (or an object with omitted fields) to keep the defaults.
   * See {@link RowStyle}.
   */
  rowStyle?: (row: T) => RowStyle | undefined;
  /**
   * Semantic classes for a rendered body row. Applied alongside the table's
   * own cursor/selection classes and `rowStyle` CSS variables.
   */
  rowClass?: (row: T) => RowClass | undefined;
  /**
   * Opens a row as a link: the address of the row's page, or `null` / `undefined`
   * for a row that has none (a view-mode table only). Every display cell of a row
   * that has one is drawn as a real `<a href>`, so the browser's own gestures work:
   * Ctrl/Cmd-click and the middle button open it in a new tab (Shift-click in a new
   * window), the address shows in the status bar and in the context menu.
   *
   * The table keeps its own gestures on a plain click: the cursor, the selection,
   * and a double-click or Enter that emits `cell-double-click` — the app navigates
   * there in the same tab. A plain click and a plain Enter never follow the link.
   * Ctrl/Cmd/Shift-click on a link cell is the browser's and does not toggle or
   * extend the row selection; a drag selection may start on a link cell.
   * Ctrl/Cmd/Shift+Enter with the focus on a link is the browser's too and emits
   * nothing (with the focus elsewhere in the table it still emits). Activating the
   * link by keyboard or assistive technology (a click with `detail === 0`, e.g. a
   * screen reader in browse mode) opens the row like Enter: one `cell-double-click`.
   *
   * The link keeps the cell's look (text colour, no underline) and is no Tab stop of
   * its own: the table owns focus. For a screen reader every display cell of such a
   * row is a link to the record. A column whose cell holds a button, a link or a
   * field of its own must set `rowLinkDisabled` — nested interactive content inside
   * an anchor is not allowed.
   *
   * Called for every cell on every render: keep it cheap (a lookup, no work); a row
   * whose address appears or disappears re-renders its cells. Default: no link.
   */
  rowHref?: (row: T) => string | null | undefined;
  /**
   * The table lists records and `cell-double-click` opens one: the gestures follow
   * `table.row_open` and the row menu offers the targets (`rowOpen.ts`). A picker,
   * where a double-click means "take this", must not set it.
   */
  recordList?: boolean;

  /** Hide the column header and quick-filter row without changing body or keyboard logic. */
  hideHeader?: boolean;
  /**
   * `list` draws every row as one full-width card: the single column fills the
   * row (no column width, no horizontal scroll), the indicator/checkbox/action
   * gutters and the cell padding go away, and a selected row carries its marker
   * on the row edge. For inbox-style lists (mail) together with `hideHeader`.
   * Defaults to `grid`.
   */
  layout?: 'grid' | 'list';
  /**
   * Fill the container: the table takes its container's height (as a flex
   * item it grows into the free space, never beyond the container) and scrolls
   * its rows inside. For a table that is the main content of a page or a
   * fixed-height panel; give the container a bounded height (a flex column
   * with `min-height: 0`, or an explicit height). Unset, the table keeps its
   * default sizing.
   */
  fill?: boolean;

  /**
   * The table's own settings key (I-2510), e.g. `erp.grid.quotes`: its named
   * filter sets ("Gespeicherte Filter") live there in `filter_sets`, a map by
   * id, read and written through the kit's settings contract (`useNxSettings`)
   * — so they follow the account wherever the app provides settings. Without
   * it they only live as long as the table.
   */
  settingsKey?: string;

  /** Title of the built-in empty state (no rows, no active filters). Defaults to the generic i18n text; the `#empty` slot replaces the whole block. */
  emptyTitle?: string;
  /** Description of the built-in empty state. Defaults to the generic i18n text. */
  emptyDescription?: string;
};

export type NxTableEmits<T extends Record<string, unknown>> = {
  /** Emitted whenever the user single-clicks a cell or presses any arrow key to select another cell. */
  'cell-single-click': [
    rowId: string,
    columnId: string,
    rowData: T,
    event?: MouseEvent | KeyboardEvent,
  ];
  /** Emitted whenever the user double-clicks a cell or presses `Enter` on a selected cell. */
  'cell-double-click': [rowId: string, columnId: string, rowData: T];
  /** Emitted whenever a user resizes a column.  */
  'column-resize': [columnId: string, newWidth: number];
  /** Emitted whenever a user moves a column. All new positions will be emitted (not just the moved one).  */
  'column-moved': [newPositions: Map<string, number>];
  /** Emitted when the user pins/unpins columns in the column dialog (changed ones only). */
  'column-sticky-changed': [newSticky: Map<string, boolean>];
  /**
   * Emitted whenever a user shows/hides columns (via the show/hide modal).
   * If a column for which sorting has been specified is hidden, `newSort` will not be empty.
   */
  'column-visibility-changed': [
    newVisibilities: Map<string, boolean>,
    newSorts: Map<string, { order?: SortOrder | null; prio?: number | null }>,
  ];
  /** Emitted whenever a user changes the sort of the table. */
  'table-sort-changed': [newSorts: Map<string, { order?: SortOrder | null; prio?: number | null }>];
  /** Emitted whenever the row selection changes. */
  'selection-change': [selectedIds: Set<string>, selectedData: T[]];
  /**
   * A drag selection (`dragSelection`) finished: the mouse button came up. The
   * selection is the final one; `selection-change` already reported every step.
   * Not emitted when Escape cancelled the drag.
   */
  'drag-selection-end': [selectedIds: Set<string>, selectedData: T[]];
  /** The set of open rows (`expandableRows`) changed. */
  'row-expanded-change': [expandedRowIds: Set<string>];
  /** Emitted when the focused body row changes; `null` means no body row is active. */
  'active-row-change': [rowId: string | null, rowData: T | null];
  /**
   * A selected read-only row received Delete/Backspace while no `onDelete`
   * handler was supplied. Consumers own the destructive action in this case.
   */
  'row-delete-request': [rowIds: string[]];
  /** `deleteGuard` refused a delete before its confirmation; `reason` is its text. */
  'delete-refused': [reason: string];
  /** Native context-menu request for any non-placeholder body row. */
  'row-context-menu': [
    payload: { rowId: string; rowData: T; x: number; y: number; event: MouseEvent },
  ];
  /**
   * rAF-coalesced virtual row window, with an exclusive `endIndex`. Counts
   * display positions — the indices of `data` unless the table is grouped.
   */
  'visible-range-change': [range: NxTableVisibleRange];
  /**
   * User asked to restore the table's factory layout (columns, widths, sort,
   * filters). Consumers that persist prefs (DataTable) wipe the saved blob
   * and re-seed from the catalog descriptor.
   */
  'reset-layout': [];
  /**
   * User picked „Breiten zurücksetzen" in the gear menu: every column width
   * went back to its measured auto width. Consumers that persist widths drop
   * them; nothing else of the layout changed.
   */
  'column-widths-reset': [];
};

export type RefetchOptions = {
  /** Reset pagination to offset 0. Defaults to `true`. */
  resetPagination?: boolean;
  /** Clear all active filters. Defaults to `false`. */
  resetFilters?: boolean;
  /** Clear all active sorts. Defaults to `false`. */
  resetSort?: boolean;
};

export type NxTableExpose<T extends Record<string, unknown>> = {
  /** Refetch data from the server. By default resets pagination but keeps filters and sort. */
  refetch: (options?: RefetchOptions) => Promise<void>;
  /**
   * Vertical scroll position of the table (overflow, offset, remaining pixels),
   * updated on scroll and on size changes. The public way to react to scrolling
   * — never query the table's scroller element. Read through a template ref it
   * is already unwrapped.
   */
  scrollState: Readonly<Ref<NxTableScrollState>>;
  /**
   * A write landed: shows a green check in the table's top-right corner (the
   * header cell opposite the settings gear) for a moment, and announces it to
   * screen readers. Cells themselves are never painted green.
   */
  showSaved: () => void;
  /**
   * Returns the rendered `<td>` element (actually a `<div>`) for the given cell, or null if the row or column
   * is not currently visible/rendered. Columns are resolved the same way as in setActiveCell.
   */
  getCellElement: (rowId: string, columnId?: string) => HTMLElement | null;
  /**
   * Focuses the cell for the given (rowId, columnId). The target cell's
   * registered focus-handler is invoked with `from` (defaults to `'click'`,
   * i.e. preserve native caret). No-op when the row/column is not navigable.
   * Passing `null` uses the row-indicator anchor.
   */
  focusedCell: ComputedRef<CellId | null>;
  /**
   * The row height step in effect: the user's choice for every table, else the
   * table's own. Lets a host drop what no longer fits, like a second cell line.
   */
  rowDensity: ComputedRef<NxTableRowDensity>;
  focusCell: (
    rowId: string,
    columnId: string | null,
    options?: { from?: FocusFrom }
  ) => void | Promise<void>;
  /**
   * Drops the cell cursor, so no row is marked, and cancels an in-flight
   * `focusCell` transition. The counterpart to `focusCell` for a caller that
   * mirrors an outside selection: `focusCell` is a no-op for a row outside the
   * loaded page, which leaves the PREVIOUS row marked — call this instead of
   * letting a wrong marking stand.
   */
  clearCursor: () => void;
  /** Focuses the last active body cell (or first editable body cell); view tables fall back to filters. */
  focusFirstCell: () => void;
  /**
   * Mark the table as the focused wheel/keyboard target (same as clicking it).
   * Wrapper-only — does not move the cell cursor. Section fullscreen also
   * calls `focusFirstCell` so arrow keys / typing work immediately.
   */
  claimTableFocus: (opts?: { focus?: boolean }) => void;
  /** Drop table focus so page scroll / other surfaces own the wheel again. */
  releaseTableFocus: () => void;
  /** Function to get the current table state */
  getTableState: () => TableMetadataState<T>;
  /**
   * Function to set parts of the table state. This should only be used if the data provided conforms with this state i.e. has the same filters, sorts, ...
   * A `filterValues` map replaces every quick filter: a column it leaves out is cleared.
   */
  setTableState: (
    state: Partial<
      Omit<TableMetadataState<T>, 'columns'> & {
        columns?: (Partial<Omit<ColumnMetadataState<T>, 'columnId'>> &
          Pick<ColumnMetadataState<T>, 'columnId'>)[];
      }
    >
  ) => void;
  /** Function to trigger a creation (useful if the creation button is hidden i.e. it does the same as clicking on the creation button) */
  startCreation: () => void;
  /**
   * Create a row from outside (focus/selection stay table-owned).
   * `focusColumnId` is the column to land on after create. The table sets the
   * cursor first (so `lazyEditCells` mounts that row) and then focuses the
   * registered edit input. Defaults to the first focusable column.
   *
   * Resolves `true` when the row was created, `false` when `onCreate` answered
   * `undefined` — the consumer refused the write and has reported why (I-1935).
   * It does not reject for a refusal: see `onCreate` for why that would take a
   * layout-hosted section off the screen.
   */
  doCreation: (
    rowToCreate: PartialOrNull<T>,
    options?: { focusColumnId?: string }
  ) => Promise<boolean>;
  /** Function to trigger a deletion from outside (useful as focus/selection are managed by the table). Will open the delete dialog. */
  askDoDeletionOfSelected: () => void;
  /**
   * Scrolls the virtual list to the given row (its index in `data`, also when
   * grouped). Returns a Promise that resolves after the scroll and the target
   * rows are rendered.
   */
  scrollTo: (rowIdx: number | 'top' | 'bottom') => Promise<void>;
  /**
   * Load every remaining server page until the grid holds the full result set
   * (or growth stops). Use before `scrollTo('bottom')` when lazy pagination
   * would otherwise leave the scroller short of the true last row.
   */
  ensureFullyLoaded: () => Promise<void>;
  /**
   * Current virtual-list window into `data` (end exclusive). Used by windowed /
   * sparse loaders to fetch only the visible band. A grouped table counts
   * display positions here (see `groupBy`).
   */
  virtualWindow: ComputedRef<{ start: number; end: number; topPad: number; bottomPad: number }>;
  /** The currently selected row IDs. Only available when `allowRowSelection` is true. */
  selectedIds: ComputedRef<Set<string>>;
  /**
   * The currently selected row data (in display order when grouped). Only
   * available when `allowRowSelection` is true.
   */
  selectedData: ComputedRef<T[]>;
  /** Select all visible rows. Only available when `allowRowSelection` is true. */
  selectAll: () => void;
  /** Select row. Only available when `allowRowSelection` is true. */
  selectRow: (rowId: string) => void;
  /** Deselect all rows. Only available when `allowRowSelection` is true. */
  deselectAll: () => void;
  /** Replace the selected row ids with the loaded, non-placeholder ids from an external owner. */
  setSelectedIds: (ids: Iterable<string>) => void;
  /** Ids of the rows whose detail is open (`expandableRows`). */
  expandedRowIds: ComputedRef<Set<string>>;
  /** Open or close the detail of row `rowId`; rows `expandableRows` rejects stay closed. */
  setRowExpanded: (rowId: string, expanded: boolean) => void;
  /** Open a closed detail, close an open one. */
  toggleRowExpanded: (rowId: string) => void;
  /** Close every open detail. */
  collapseAllRows: () => void;
};

export * from '../internal';
